import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Upload, FileCheck2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient, formatApiError } from "@/lib/api";
import { SUPPORT_TYPES, FALLBACK_DEADLINES } from "./data";
import { SectionHeading } from "./Services";

const inputCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
const selectCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
const contentCls = "border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]";

export const UploadBrief = () => {
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", university: "", course: "",
    support_type: SUPPORT_TYPES[0], word_count: 2000, deadline: FALLBACK_DEADLINES[2].label,
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await apiClient.post("/briefs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Brief received! We'll reply with a quote within 15 minutes.");
      setForm({ name: "", email: "", whatsapp: "", university: "", course: "", support_type: SUPPORT_TYPES[0], word_count: 2000, deadline: FALLBACK_DEADLINES[2].label });
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err, "Could not upload your brief. Please try WhatsApp instead."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="upload-brief" className="border-y border-[#D4AF37]/10 bg-[#F3EEE2]" data-testid="upload-brief-section">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <SectionHeading id="upload-brief" overline="Smart Requirement Upload" title="Upload your brief." accent="Skip the back-and-forth." />
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
            className="lg:col-span-2"
          >
            <p className="max-w-md text-base leading-relaxed text-[#46586F]">
              Attach your assignment brief, rubric or module handbook (PDF, DOC or DOCX — up to 15 MB).
              The more detail you share, the sharper your quote and the faster we match your expert.
            </p>
            <div className="mt-8 space-y-5">
              {[
                "Your file is stored securely and never shared",
                "A subject specialist reviews it before quoting",
                "Fixed price — no surprises later",
              ].map((t) => (
                <p key={t} className="flex items-center gap-3 text-sm text-[#46586F]">
                  <ShieldCheck size={16} className="shrink-0 text-[#A07E1F]" /> {t}
                </p>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.33, 1, 0.68, 1] }}
            className="luxury-card rounded-xl p-6 sm:p-10 lg:col-span-3"
            data-testid="brief-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="brief-university" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">University *</Label>
                <Input id="brief-university" required value={form.university} onChange={(e) => set("university", e.target.value)} data-testid="brief-university-input" placeholder="e.g. University of Manchester" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brief-course" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Course / Module *</Label>
                <Input id="brief-course" required value={form.course} onChange={(e) => set("course", e.target.value)} data-testid="brief-course-input" placeholder="e.g. Strategic Management" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Type of Support *</Label>
                <Select value={form.support_type} onValueChange={(v) => set("support_type", v)}>
                  <SelectTrigger data-testid="brief-support-select" className={selectCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {SUPPORT_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`brief-support-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Deadline *</Label>
                <Select value={form.deadline} onValueChange={(v) => set("deadline", v)}>
                  <SelectTrigger data-testid="brief-deadline-select" className={selectCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {FALLBACK_DEADLINES.map((d) => <SelectItem key={d.value} value={d.label} data-testid={`brief-deadline-${d.value}`}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="brief-words" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Word Count *</Label>
                <Input id="brief-words" type="number" min={100} step={50} required value={form.word_count} onChange={(e) => set("word_count", e.target.value)} data-testid="brief-words-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brief-name" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Your Name *</Label>
                <Input id="brief-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="brief-name-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brief-email" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Email *</Label>
                <Input id="brief-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="brief-email-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brief-whatsapp" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">WhatsApp Number *</Label>
                <Input id="brief-whatsapp" required value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="brief-whatsapp-input" placeholder="+44 ..." className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="brief-file" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Brief File (PDF / DOC / DOCX)</Label>
                <label
                  htmlFor="brief-file"
                  data-testid="brief-file-dropzone"
                  className="flex cursor-pointer items-center justify-center gap-3 rounded-lg border border-dashed border-[#D4AF37]/40 bg-white/70 px-6 py-8 text-sm text-[#46586F] transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#12294D]"
                >
                  {file ? <FileCheck2 size={18} className="text-[#A07E1F]" /> : <Upload size={18} className="text-[#A07E1F]" />}
                  {file ? file.name : "Click to attach your assignment brief"}
                </label>
                <input
                  id="brief-file"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="brief-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="brief-submit-button"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 font-mono-accent text-xs font-bold uppercase tracking-[0.2em] text-[#0A1E3C] transition-colors duration-300 hover:bg-[#C9A227] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {loading ? "Uploading..." : "Submit Your Requirement"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
